import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { applyCachedColorProfile, rememberColorProfile } from './colorProfile';
import { useTheme } from './theme';
import { api, tokenStore } from './api';

export type Scope = 'own' | 'assigned' | 'objects' | 'tenant' | 'all';

export interface CurrentUser {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  tenantId: number | null;
  isSuperadmin: boolean;
  /** Aktive Testsicht auf eine Rolle – trägt das Hinweisband der Kopfzeile. */
  rollensicht?: { id: number; key: string; name: string } | null;
  roles: { id: number; key: string; name: string }[];
  groups: { id: number; key: string; name: string; inherited: boolean }[];
  propertyIds: number[];
  permissions: Record<string, { effect: 'allow' | 'deny'; scope: Scope }>;
  theme?: string;
  /** Eigenes Farbprofil; fehlt es, gilt das der Organisation. */
  color_profile?: string | null;
  phone?: string;
  job_title?: string;
  two_factor_enabled?: number;
  /**
   * Ausstehender Passwortwechsel (Review M1).
   *
   * Seit der Härtung setzt die Middleware das serverseitig durch: Bis zur
   * Änderung antwortet jede Anfrage außer `/me`, `/me/password` und
   * `/logout` mit `password_change_required`. Die Oberfläche zeigt deshalb
   * den Wechsel als Sperrbildschirm, statt in Fehlermeldungen zu laufen.
   */
  must_change_password?: number;
}

interface AuthContextValue {
  user: CurrentUser | null;
  tenant: { id: number; name: string; code: string; colorProfile?: string } | null;
  loading: boolean;
  login: (email: string, password: string, totp?: string) => Promise<{ twoFactorRequired?: boolean }>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  can: (permission: string) => boolean;
  canAny: (...permissions: string[]) => boolean;
  scopeOf: (permission: string) => Scope | null;
  /**
   * Konto ohne Mandant und ohne mandantenübergreifende Rolle: stillgelegt.
   *
   * Der Zugang bleibt bestehen, Fachdaten gibt es keine – jede Abfrage
   * braucht einen Mandanten. Statt überall Fehlermeldungen zu zeigen, sperrt
   * die Oberfläche die Bereiche sichtbar und lässt das Profil offen.
   */
  isParked: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [tenant, setTenant] = useState<AuthContextValue['tenant']>(null);
  const [loading, setLoading] = useState(true);
  // `ThemeProvider` liegt in `main.tsx` über dem `AuthProvider` – der Zugriff
  // ist hier also gesichert.
  const { adoptFromAccount } = useTheme();

  const loadMe = useCallback(async () => {
    if (!tokenStore.access) {
      setUser(null);
      // Ohne Konto trägt die Anmeldeseite die zuletzt gesehene Hausfarbe,
      // statt sichtbar auf Blau zurückzufallen.
      applyCachedColorProfile();
      setLoading(false);
      return;
    }
    try {
      const data = await api.get('/api/auth/me');
      setUser(data.user);
      setTenant(data.tenant);
      // Farbprofil sofort ans Wurzelelement, nicht erst über einen Effekt in
      // einer Komponente: Sonst zeigt die Anwendung für einen Bildaufbau das
      // Blau, bevor sie auf die Hausfarbe springt.
      rememberColorProfile(data.user?.color_profile ?? data.tenant?.colorProfile);
      // Hell/Dunkel gehört dem Konto und folgt ihm auf jedes Gerät. Bewusst
      // über `adoptFromAccount`, nicht über `setPreference`: Letzteres
      // schriebe den gerade gelesenen Wert sofort wieder zurück.
      adoptFromAccount(data.user?.theme);
    } catch {
      tokenStore.clear();
      setUser(null);
      applyCachedColorProfile();
    } finally {
      setLoading(false);
    }
  }, [adoptFromAccount]);

  useEffect(() => {
    void loadMe();
    const onUnauthorized = () => setUser(null);
    window.addEventListener('ff:unauthorized', onUnauthorized);
    return () => window.removeEventListener('ff:unauthorized', onUnauthorized);
  }, [loadMe]);

  const login = useCallback(
    async (email: string, password: string, totp?: string) => {
      const data = await api.post('/api/auth/login', { email, password, totp });
      if (data.twoFactorRequired) return { twoFactorRequired: true };
      tokenStore.set(data.accessToken, data.refreshToken);
      setUser(data.user);
      await loadMe();
      return {};
    },
    [loadMe],
  );

  const logout = useCallback(async () => {
    try {
      await api.post('/api/auth/logout', { refreshToken: tokenStore.refresh });
    } catch {
      /* Abmeldung soll auch offline funktionieren */
    }
    tokenStore.clear();
    setUser(null);
    setTenant(null);
  }, []);

  const value = useMemo<AuthContextValue>(() => {
    const can = (permission: string) => user?.permissions?.[permission]?.effect === 'allow';
    return {
      user,
      tenant,
      isParked: Boolean(user && user.tenantId == null && !user.isSuperadmin),
      loading,
      login,
      logout,
      refreshUser: loadMe,
      can,
      canAny: (...permissions: string[]) => permissions.some(can),
      scopeOf: (permission: string) =>
        user?.permissions?.[permission]?.effect === 'allow'
          ? user.permissions[permission].scope
          : null,
    };
  }, [user, tenant, loading, login, logout, loadMe]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth muss innerhalb von <AuthProvider> verwendet werden.');
  return context;
}
